/**
 * LLM-powered page translator — replaces the Google Translate widget.
 *
 * Walks visible text nodes, batches unique English strings to
 * POST /api/i18n/translate (GPT-translated once, DB-cached forever),
 * swaps them in place, and keeps up with React re-renders via a
 * MutationObserver.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const APP_LANGS = [
    { code: "en", label: "English", native: "English" },
    { code: "ar", label: "Arabic", native: "العربية", rtl: true },
    { code: "uz", label: "Uzbek", native: "O‘zbekcha" },
    { code: "ru", label: "Russian", native: "Русский" },
    { code: "zh", label: "Chinese", native: "中文" },
    { code: "es", label: "Spanish", native: "Español" },
    { code: "de", label: "German", native: "Deutsch" },
    { code: "fr", label: "French", native: "Français" },
];

const LANG_KEY = "app_lang";

export function getLang() {
    try {
        const v = localStorage.getItem(LANG_KEY) || "en";
        return APP_LANGS.some((l) => l.code === v) ? v : "en";
    } catch {
        return "en";
    }
}

export function setLang(code) {
    try { localStorage.setItem(LANG_KEY, code); } catch { /* private mode */ }
    window.location.reload();
}

const cache = new Map();          // english → translated (session memory)
const done = new WeakSet();       // text nodes already processed
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "PRE", "SVG", "IFRAME"]);
const CAT_TTL_MS = 24 * 60 * 60 * 1000; // refetch catalogs daily
let applying = false;
let flushTimer = null;
let inFlight = false;
let rerunQueued = false;
let retryCount = 0;

/* ── Catalog prefetch: all languages downloaded in the background on first
      visit and kept in localStorage, so switching is instant with no
      untranslated flash. ─────────────────────────────────────────────── */

function catKey(lang) { return `i18n_cat_${lang}`; }

function loadCatalogIntoCache(lang) {
    try {
        const raw = localStorage.getItem(catKey(lang));
        if (!raw) return false;
        const { entries } = JSON.parse(raw);
        Object.entries(entries || {}).forEach(([k, v]) => cache.set(k, v));
        return true;
    } catch {
        return false;
    }
}

async function fetchCatalog(lang, { intoCache = false } = {}) {
    try {
        const res = await fetch(`${API}/i18n/catalog/${lang}`);
        if (!res.ok) return;
        const data = await res.json();
        try {
            localStorage.setItem(catKey(lang), JSON.stringify({ t: Date.now(), entries: data.entries }));
        } catch { /* quota — in-memory only */ }
        if (intoCache) Object.entries(data.entries || {}).forEach(([k, v]) => cache.set(k, v));
    } catch { /* offline — retried next visit */ }
}

function catalogIsFresh(lang) {
    try {
        const raw = localStorage.getItem(catKey(lang));
        if (!raw) return false;
        return Date.now() - (JSON.parse(raw).t || 0) < CAT_TTL_MS;
    } catch {
        return false;
    }
}

function schedulePrefetchAll(currentLang) {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 3500));
    idle(() => {
        const langs = APP_LANGS.map((l) => l.code).filter((c) => c !== "en" && c !== currentLang);
        langs.forEach((lang, i) => {
            if (catalogIsFresh(lang)) return;
            setTimeout(() => fetchCatalog(lang), i * 900);
        });
    });
}

function shouldTranslate(text) {
    const t = text.trim();
    if (!t || t.length < 2) return false;
    if (!/[A-Za-z]{2,}/.test(t)) return false;                       // numbers / symbols / non-latin
    if (/^[A-Z0-9 .,:;%+×·\-–—/()#&$€£']+$/.test(t) && t.length <= 5) return false; // short codes: QAR, PDF...
    if (/^(https?:\/\/|www\.)/i.test(t)) return false;
    if (/^[\w.+-]+@[\w-]+\.[\w.]+$/.test(t)) return false;
    return true;
}

function collectTextNodes(root) {
    const nodes = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(n) {
            const p = n.parentElement;
            if (!p) return NodeFilter.FILTER_REJECT;
            if (SKIP_TAGS.has(p.tagName)) return NodeFilter.FILTER_REJECT;
            if (p.closest('.notranslate,[data-notranslate],[translate="no"]')) return NodeFilter.FILTER_REJECT;
            if (done.has(n)) return NodeFilter.FILTER_REJECT;
            if (!shouldTranslate(n.nodeValue)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
        },
    });
    while (walker.nextNode()) nodes.push(walker.currentNode);
    return nodes;
}

function collectAttrTargets(root) {
    const out = [];
    const els = root.querySelectorAll("[placeholder], [title], [aria-label]");
    els.forEach((el) => {
        if (el.closest('.notranslate,[data-notranslate],[translate="no"]')) return;
        ["placeholder", "title", "aria-label"].forEach((attr) => {
            const v = el.getAttribute(attr);
            if (v && shouldTranslate(v) && el.getAttribute(`data-i18n-${attr}`) !== "1") {
                out.push({ el, attr, text: v.trim() });
            }
        });
    });
    return out;
}

async function fetchTranslations(lang, texts) {
    for (let i = 0; i < texts.length; i += 250) {
        const chunk = texts.slice(i, i + 250);
        try {
            const res = await fetch(`${API}/i18n/translate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ lang, texts: chunk }),
            });
            if (!res.ok) continue;
            const data = await res.json();
            Object.entries(data.translations || {}).forEach(([k, v]) => cache.set(k, v));
        } catch {
            /* network hiccup — strings stay English, retried on next pass */
        }
    }
}

async function translatePass(lang) {
    if (inFlight) { rerunQueued = true; return; }
    inFlight = true;
    try {
        const nodes = collectTextNodes(document.body);
        const attrs = collectAttrTargets(document.body);
        if (!nodes.length && !attrs.length) return;

        const wanted = new Set();
        nodes.forEach((n) => { const k = n.nodeValue.trim(); if (!cache.has(k)) wanted.add(k); });
        attrs.forEach(({ text }) => { if (!cache.has(text)) wanted.add(text); });
        if (wanted.size) await fetchTranslations(lang, [...wanted]);

        applying = true;
        let missing = 0;
        nodes.forEach((n) => {
            const raw = n.nodeValue;
            const key = raw.trim();
            if (cache.has(key)) {
                const tr = cache.get(key);
                if (tr && tr !== key) n.nodeValue = raw.replace(key, tr);
                done.add(n); // only settle nodes whose translation actually arrived
            } else {
                missing += 1;
            }
        });
        attrs.forEach(({ el, attr, text }) => {
            if (cache.has(text)) {
                const tr = cache.get(text);
                if (tr && tr !== text) el.setAttribute(attr, tr);
                el.setAttribute(`data-i18n-${attr}`, "1");
            } else {
                missing += 1;
            }
        });
        // A chunk failed or is still in flight — retry soon instead of
        // leaving English text stranded until the next DOM mutation.
        if (missing > 0 && retryCount < 5) {
            retryCount += 1;
            setTimeout(() => translatePass(lang), 2500);
        } else if (missing === 0) {
            retryCount = 0;
        }
        // let the observer flush our own mutations before re-listening
        setTimeout(() => { applying = false; }, 0);
    } finally {
        inFlight = false;
        if (rerunQueued) { rerunQueued = false; translatePass(lang); }
    }
}

const ARABIC_FONTS_ID = "arabic-webfonts";
const ARABIC_FONTS_HREF =
    "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&family=Cairo:wght@500;600;700;800&family=Tajawal:wght@400;500;700&display=swap";

/** Load professional Arabic webfonts once, only when Arabic is active. */
export function ensureArabicFonts() {
    if (document.getElementById(ARABIC_FONTS_ID)) return;
    const pre = document.createElement("link");
    pre.rel = "preconnect";
    pre.href = "https://fonts.gstatic.com";
    pre.crossOrigin = "anonymous";
    document.head.appendChild(pre);
    const link = document.createElement("link");
    link.id = ARABIC_FONTS_ID;
    link.rel = "stylesheet";
    link.href = ARABIC_FONTS_HREF;
    document.head.appendChild(link);
}

export function initTranslator() {
    // kill any legacy Google Translate cookie so the old widget never fights us
    try {
        const host = window.location.hostname;
        const root = host.split(".").length > 1 ? "." + host.split(".").slice(-2).join(".") : host;
        document.cookie = "googtrans=; path=/; max-age=0";
        document.cookie = `googtrans=; path=/; domain=${root}; max-age=0`;
    } catch { /* noop */ }

    const lang = getLang();
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
    if (lang === "ar") ensureArabicFonts();
    // prefetch every other language while the user reads — switch = instant
    schedulePrefetchAll(lang);
    if (lang === "en") return;

    // catalog-first boot: apply the locally stored dictionary immediately
    // (no network round-trip → no untranslated flash), refresh it after.
    loadCatalogIntoCache(lang);
    fetchCatalog(lang, { intoCache: true });

    const run = () => translatePass(lang);
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", run, { once: true });
    } else {
        run();
    }
    const observer = new MutationObserver(() => {
        if (applying) return;
        clearTimeout(flushTimer);
        flushTimer = setTimeout(run, 300);
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}
